import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import { adminService } from '../../../services/adminService';

type Job = 'option-chain' | 'max-pain' | 'earnings-calendar' | 'market-pulse';

const JOBS: { job: Job; label: string; description: string }[] = [
  {
    job: 'option-chain',
    label: 'Fetch Option Chains',
    description: 'Pulls the latest option chain snapshots from Tradier for all active tickers. Runs automatically every hour at :05 during market hours.',
  },
  {
    job: 'earnings-calendar',
    label: 'Sync Earnings Calendar',
    description: 'Fetches upcoming earnings dates via yfinance for all active tickers. Runs automatically daily at 8:00 AM ET.',
  },
  {
    job: 'market-pulse',
    label: 'Compute Market Pulse',
    description: 'Analyzes OI changes, GEX, volume, and IV for all active tickers. Runs automatically daily at 9:20 AM ET after the pre-open chain collection.',
  },
];

function tomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

const JobTriggers: React.FC = () => {
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, 'ok' | 'error'>>({});
  const [analyzerResult, setAnalyzerResult] = useState<any[] | null>(null);
  const [analyzerDate, setAnalyzerDate] = useState(tomorrow());
  const [deltaThreshold, setDeltaThreshold] = useState('0.05');
  const [deltaThresholdSaved, setDeltaThresholdSaved] = useState('0.05');
  const [deltaSaving, setDeltaSaving] = useState(false);

  useEffect(() => {
    adminService.getConfig('max_pain_delta_threshold').then((c) => {
      setDeltaThreshold(c.value);
      setDeltaThresholdSaved(c.value);
    }).catch(() => {});
  }, []);

  const handleRun = async (job: Job) => {
    setRunningJob(job);
    setResults((prev) => { const n = { ...prev }; delete n[job]; return n; });
    try {
      await adminService.runJob(job);
      setResults((prev) => ({ ...prev, [job]: 'ok' }));
    } catch {
      setResults((prev) => ({ ...prev, [job]: 'error' }));
    } finally {
      setRunningJob(null);
    }
  };

  const handleRunAnalyzer = async () => {
    setRunningJob('earnings-analyzer');
    setAnalyzerResult(null);
    setResults((prev) => { const n = { ...prev }; delete n['earnings-analyzer']; return n; });
    try {
      const resp = await adminService.runEarningsAnalyzer(analyzerDate) as any;
      setAnalyzerResult(resp.results ?? []);
      setResults((prev) => ({ ...prev, 'earnings-analyzer': 'ok' }));
    } catch {
      setResults((prev) => ({ ...prev, 'earnings-analyzer': 'error' }));
    } finally {
      setRunningJob(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 680 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Manual Job Triggers</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        All jobs run automatically on schedule. Use these to trigger them immediately — useful after adding new tickers or during debugging.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {JOBS.map(({ job, label, description }) => (
          <Card variant="outlined" key={job}>
            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>{description}</Typography>
                {results[job] === 'ok' && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Completed successfully</Alert>}
                {results[job] === 'error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Job failed — check server logs</Alert>}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={runningJob === job ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                disabled={!!runningJob}
                onClick={() => handleRun(job)}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {runningJob === job ? 'Running…' : 'Run Now'}
              </Button>
            </CardContent>
          </Card>
        ))}

        {/* Max Pain — with configurable delta threshold */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Recalculate Max Pain</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5} mb={1.5}>
                  Recomputes max pain strikes from the most recent option chain snapshots.
                  Only strikes with |delta| &ge; the threshold below are included — filters out deep OTM noise.
                  Runs automatically every hour at :15 during market hours.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Min |delta|"
                    value={deltaThreshold}
                    onChange={(e) => setDeltaThreshold(e.target.value)}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    sx={{ width: 130 }}
                    InputProps={{
                      endAdornment: deltaThreshold !== deltaThresholdSaved ? (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            variant="text"
                            disabled={deltaSaving}
                            sx={{ minWidth: 0, p: 0.5 }}
                            onClick={async () => {
                              setDeltaSaving(true);
                              try {
                                await adminService.setConfig('max_pain_delta_threshold', deltaThreshold);
                                setDeltaThresholdSaved(deltaThreshold);
                              } finally {
                                setDeltaSaving(false);
                              }
                            }}
                          >
                            {deltaSaving ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                          </Button>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Box>
                {results['max-pain'] === 'ok' && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Completed successfully</Alert>}
                {results['max-pain'] === 'error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Job failed — check server logs</Alert>}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={runningJob === 'max-pain' ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                disabled={!!runningJob}
                onClick={() => handleRun('max-pain')}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {runningJob === 'max-pain' ? 'Running…' : 'Run Now'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Earnings Analyzer — separate because it takes a date */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600}>Run Earnings Analyzer</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5} mb={1.5}>
              Analyzes IV edge, grades, and strategy recommendations for earnings on the selected date. Runs automatically daily at 9:45 AM ET for the next day's earnings.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                type="date"
                size="small"
                label="Earnings date"
                value={analyzerDate}
                onChange={(e) => setAnalyzerDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={runningJob === 'earnings-analyzer' ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                disabled={!!runningJob}
                onClick={handleRunAnalyzer}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {runningJob === 'earnings-analyzer' ? 'Running…' : 'Run Now'}
              </Button>
            </Box>
            {results['earnings-analyzer'] === 'error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Job failed — check server logs</Alert>}
            {analyzerResult !== null && (
              analyzerResult.length === 0
                ? <Alert severity="warning" sx={{ mt: 1, py: 0 }}>No earnings events found for this date</Alert>
                : analyzerResult.map((r: any) => (
                  <Alert
                    key={r.ticker}
                    severity={r.status === 'ok' ? 'success' : 'error'}
                    sx={{ mt: 1, py: 0 }}
                  >
                    {r.ticker}: {r.status === 'error' ? r.error : `grade=${r.grade ?? 'S'} · strategies=${r.strategies ?? 0} · hist_move=${r.hist_avg_move_available ? 'yes' : 'no (fallback used)'}`}
                  </Alert>
                ))
            )}
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        Option chain fetch can take 30–60 seconds depending on the number of active tickers. The page will not freeze — the job runs on the server.
      </Typography>
    </Box>
  );
};

export default JobTriggers;
